import http from "./httpService";

const apiEndPoint = "/locations/remotecmd";

function timeout(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Stable id for Amplitude correlation between Manager API and on-box update-dvr (Taxi path). */
function newCorrelationId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

async function triggerOnPremRemoteCommand(data, { signal, pollIntervalMs = 2000, onProgress } = {}) {
  const body = { ...data, timestamp: Date.now() };
  if (data.cmd === "update-dvr") {
    body.cmdArgs = {
      ...body.cmdArgs,
      correlation_id: body.cmdArgs?.correlation_id || newCorrelationId(),
    };
  }

  Object.keys(body).forEach(function (key) {
    if (body[key] === "" || typeof body[key] === "undefined") {
      body[key] = null;
    }
  });
  if (data.cmd === "connect-dvr-interface") {
    const newBody = { ...body, cmd: "clean-iptable-rules" };
    await http.post(apiEndPoint, newBody);
    await timeout(500);
  }
  let response = await http.post(apiEndPoint, body);

  while (response.status === 200 && response.data.status?.toLowerCase() === "running") {
    if (onProgress) onProgress(response.data);
    await timeout(pollIntervalMs);
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
    response = await http.post(apiEndPoint, body);
  }

  return response.data;
}
export default triggerOnPremRemoteCommand;

export async function triggerCloudRemoteCommand(data, { signal } = {}) {
  const { hostname, cmdArgs } = data;
  const url = `/locations/chauffeur/dvr/update/${hostname}`;
  const payload = {
    ...cmdArgs,
    correlation_id: cmdArgs?.correlation_id || newCorrelationId(),
  };
  const response = await http.post(url, payload);

  if (response.data.operation_id) {
    return pollCloudDvrUpdateStatus(hostname, response.data.operation_id, cmdArgs.type_dvr, signal);
  }

  return {
    status: response.data.status || "OK",
    stdout: response.data.stdout || response.data,
    stderr: response.data.stderr || "",
  };
}

async function pollCloudDvrUpdateStatus(hostname, operationId, dvrType, signal) {
  const maxAttempts = 30;
  let attempts = 0;

  while (attempts < maxAttempts) {
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
    await timeout(10000);
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
    attempts++;

    try {
      const statusResponse = await http.get(
        `/locations/chauffeur/dvr/status/${hostname}/${operationId}?dvr_type=${dvrType || "hik"}`
      );

      if (statusResponse.data.status !== "running") {
        console.log(statusResponse.data);
        return {
          status: statusResponse.data.status,
          stdout: statusResponse.data,
          stderr: statusResponse.data.stderr || "",
        };
      }
    } catch (error) {
      if (error.response && error.response.status === 404) {
        console.error("Operation not found, stopping polling");
        return {
          status: "ERROR",
          stdout: "",
          stderr: "Operation not found",
        };
      }
      if (error.response && error.response.status === 400) {
        console.error("Bad request error:", error.response.data);
        return {
          status: "ERROR",
          stdout: "",
          stderr: error.response.data.stderr || error.response.data.error || JSON.stringify(error.response.data),
        };
      }
    }
  }

  console.error(`Polling timed out after ${maxAttempts} attempts`);
  return {
    status: "EXPIRED",
    stdout: "",
    stderr: "Operation timed out",
  };
}

export async function fetchUpdateDvrCapabilitiesOnPrem(hostname, cmdArgs) {
  const body = {
    hostname,
    cmd: "update-dvr",
    cmdArgs: {
      type_action: "capabilities",
      type_dvr: cmdArgs.type_dvr,
      ip_dvr: cmdArgs.ip_dvr,
      username: cmdArgs.username,
      password: cmdArgs.password,
      port: cmdArgs.port || null,
      flux: cmdArgs.flux || null,
    },
    timestamp: Date.now(),
  };

  Object.keys(body.cmdArgs).forEach((key) => {
    if (body.cmdArgs[key] === "" || typeof body.cmdArgs[key] === "undefined") {
      body.cmdArgs[key] = null;
    }
  });

  let response = await http.post(apiEndPoint, body);

  while (response.status === 200 && response.data.status?.toLowerCase() === "running") {
    await timeout(2000);
    response = await http.post(apiEndPoint, body);
  }

  const { status, stdout, stderr } = response.data;

  if (status?.toLowerCase() !== "ok") {
    throw new Error(stderr || "Failed to fetch capabilities");
  }

  if (!stdout || typeof stdout !== "string") {
    throw new Error("Invalid capabilities response");
  }

  try {
    return JSON.parse(stdout);
  } catch {
    throw new Error("Invalid JSON in capabilities response");
  }
}
